import type { Translations } from './ru';

export const de: Translations = {
  header: {
    nav: {
      howItWorks: 'So funktioniert es',
      example:    'Beispielbericht',
      faq:        'FAQ',
    },
    cta: 'Meinen Plan holen',
  },

  footer: {
    tagline: 'Wir lesen die Etiketten deiner Pflegeprodukte und erstellen eine Routine, die zu deiner Haut passt – ohne unnötige Wirkstoffe oder extra Ausgaben.',
    nav:     'Navigation',
    contact: 'Kontakt',
    privacy: 'Datenschutz',
    copyright: '© {year} SkinBloom · Persönliche Hautpflegeanalyse',
    disclaimer: 'Kein Ersatz für einen Dermatologen',
    links: {
      howItWorks: 'So funktioniert es',
      example:    'Beispielbericht',
      whyBetter:  'Warum wir',
      faq:        'FAQ',
    },
  },

  hero: {
    badge: 'Persönliche Hautpflege · 4,99 $ einmalig · 100% Rückgabe',
    headline: ['Ein Hautpflegeplan,', 'der zu dir passt —', 'mit dem, was du schon hast.'],
    subhead:  'Zeig uns deine Produkte — wir prüfen jeden Inhaltsstoff auf porenverengende Stoffe, Konflikte und was fehlt. Plan in deinem Postfach in 5 Minuten.',
    ctaPrimary:   'Meinen Plan holen',
    ctaSecondary: 'Beispiel ansehen',
    trustCount:   '12.000+ erstellte Pläne',
    badges: {
      private: 'Vertraulich',
      email:   'Per E-Mail',
      time:    '5 Minuten',
    },
    cards: {
      skinType: {
        label: 'HAUTTYP',
        value: 'Mischhaut,\nsensibel',
      },
      actives: {
        label: 'WIRKSTOFFE',
        items: ['Niacinamid', 'Ceramide', 'Hyaluronsäure'],
      },
      plan: {
        label: 'PFLEGEPLAN',
        text:  'AM · PM · 5 Schritte',
      },
      report: {
        title:  'Bericht wird erstellt',
        status: 'Fast fertig!',
      },
    },
  },

  problem: {
    titleA: 'Viele Produkte.',
    titleB: 'Zu wenig Klarheit.',
    cards: [
      { title: 'Kaufen nach Bewertungen',              text: 'Aber niemand hat dir gesagt, ob es wirklich zu deiner Haut passt.' },
      { title: 'Inhaltsstofflisten wirken fremd',      text: 'Säuren, Retinol, Ceramide, Niacinamid. Was passt womit – unklar.' },
      { title: 'Das Regal wächst, das System nicht',   text: 'Morgens vor dem Spiegel weißt du immer noch nicht, was du zuerst aufträgst und warum.' },
    ],
  },

  preview: {
    eyebrow:  'Beispielbericht',
    titleA:   'Das bekommst du',
    titleB:   'nach der Analyse',
    subtitle: 'Keine generischen Ratschläge – ein konkreter Plan: Was behalten, was weglassen, was ergänzen und wie anwenden.',
    moreNote: 'Dein vollständiger Bericht enthält 5 weitere Abschnitte: eine detaillierte Routine, eine stufenweise Aufschlüsselung, erwartete Ergebnisse und Produktempfehlungen.',
    ctaPrimary: 'Ich möchte so einen Plan',
    ctaOverlay: 'Den vollständigen Beispielbericht öffnen',

    swaps: {
      title: 'Analyse deiner Produkte',
      rows: [
        { from: 'TO Niacinamide 10% + Zinc',  to: 'G&G Stress Less',          note: '10% ist zu stark für sensible Haut' },
        { from: 'CeraVe Hydrating Cleanser',  to: 'CeraVe Hydrating Cleanser',  note: 'Gut geeignet – behalten' },
        { from: null,                          to: 'Balea Panthenol Serum',     note: 'Notwendig zur Hautbarriere-Reparatur' },
      ],
      missing: 'Nicht in deiner Routine',
    },

    ingredients: {
      title: 'Inhaltsstoffe für deine Haut',
      items: [
        { name: 'Niacinamid 2–5%',           reason: 'Reduziert Rötungen, stärkt die Barriere' },
        { name: 'Ceramide + Cholesterin',     reason: 'Bauen die Schutzschicht der Haut wieder auf' },
        { name: 'Panthenol (B5)',             reason: 'Beruhigt Reizungen, stellt Komfort wieder her' },
        { name: 'Hyaluronsäure',              reason: 'Hält Feuchtigkeit – bekämpft Spannungsgefühl' },
      ],
      caption: 'Konzentrationen und Kombinationen für sensible Haut mit Rötungen angepasst.',
    },

    routine: {
      title: 'Pflegeplan',
      tabs:  { morning: 'Morgen', evening: 'Abend' },
      steps: {
        morning: ['Reinigen', 'Niacinamid', 'Feuchtigkeitspflege', 'LSF'],
        evening: ['Reinigen', 'Panthenol', 'Creme', 'Regeneration'],
      },
    },

    timeline: {
      title: 'Wann du Ergebnisse siehst',
      steps: [
        { period: '1–2 Wo.', text: 'Weniger Spannungsgefühl, Haut wirkt ruhiger' },
        { period: '4 Wo.',   text: 'Rötungen weniger sichtbar, Haut gleichmäßiger' },
        { period: '6–8 Wo.', text: 'Barriere wiederhergestellt, Zustand stabil' },
      ],
    },
  },

  status: {
    ok:      'Gut geeignet',
    replace: 'Ersetzen',
    missing: 'Fehlt',
  },

  howItWorks: {
    titleA: 'Wie aus Chaos',
    titleB: 'ein klarer Plan wird',
    flow:   ['Deine Produkte', 'Inhaltsstoffanalyse', 'Pflegeplan'],
    steps: [
      { n: '01', title: 'Erzähl uns von deiner Haut und deinen Produkten', text: 'Hauttyp, Probleme, Ziele und was bereits in deinem Regal steht. Schreib es auf oder schick ein Foto.' },
      { n: '02', title: 'Wir lesen jedes Etikett',                         text: 'Wir sehen, was zu deiner Haut passt, was sich überschneidet, was reizt und was fehlt.' },
      { n: '03', title: 'Du bekommst einen Schritt-für-Schritt-Plan',       text: 'Was behalten, weglassen, ergänzen — und genau wie du jedes Produkt morgens und abends anwendest.' },
    ],
  },

  whyBetter: {
    titleA: 'Kein weiterer generischer Artikel.',
    titleB: 'Ein Plan für deine Haut.',
    cards: [
      { title: 'Nicht trendgetrieben',                    text: 'Wir schauen auf deine Haut und die Formel – nicht auf die Beliebtheit eines Produkts.' },
      { title: 'Kein Neumuss beim Einkaufen',             text: 'Zuerst prüfen wir, was in deiner Routine du einfach weiterbenutzen kannst.' },
      { title: 'Keine unnötigen Wirkstoffe',               text: 'Wir zeigen, was du wirklich brauchst und was nur reizen könnte.' },
      { title: 'Klare Anweisungen',                        text: 'Morgen, Abend, Häufigkeit – konkret, kein Rätselraten.' },
    ],
    tagline: 'SkinBloom verkauft dir kein neues Regal. Wir helfen dir zu sehen, was in deiner Routine bereits funktioniert – und was wirklich fehlt.',
  },

  trust: {
    titleA: 'Ehrlich, klar,',
    titleB: 'ohne Versprechen zu machen',
    cards: [
      { title: 'Kein Ersatz für einen Dermatologen',    text: 'Wenn du eine Hauterkrankung hast, geh bitte zum Arzt.' },
      { title: 'Kein Drang, alles neu zu kaufen',       text: 'Wir beginnen damit, zu analysieren, was du bereits besitzt.' },
      { title: 'Fokus auf die Formel',                  text: 'Wirkstoffe, Konzentrationen, Verträglichkeit und mögliche Reizstoffe.' },
      { title: 'Deine Daten bleiben privat',            text: 'Sie werden nur zur Erstellung deines Berichts verwendet.' },
    ],
  },

  planSection: {
    titleA:   'Fangen wir mit deiner Haut an',
    subtitle: 'Ein paar Fragen – und wir schicken deinen fertigen Plan in wenigen Minuten per E-Mail.',
  },

  faq: {
    title: 'Häufig gefragt',
    items: [
      { q: 'Ist das ein Ersatz für einen Dermatologen?',
        a: 'Nein. SkinBloom ist ein Tool zur Formelanalyse und zur Erstellung einer persönlichen Routine. Bei einer Hauterkrankung (Akne, Rosazea, Ekzem) wende dich bitte an einen Dermatologen.' },
      { q: 'Muss ich neue Produkte kaufen?',
        a: 'Nicht unbedingt. Wir analysieren zuerst, was du bereits besitzt, und zeigen, was bleiben kann. Ersatz wird nur empfohlen, wenn ein Produkt wirklich nicht passt.' },
      { q: 'Kann ich Produkte einbeziehen, die ich schon zu Hause habe?',
        a: 'Ja – genau so funktioniert die Analyse. Du listest auf, was du gerade verwendest, und wir erklären, was zu deiner Haut passt, was getauscht werden sollte und was fehlt.' },
      { q: 'Was ist im Bericht enthalten?',
        a: 'Ein persönliches Hautprofil, eine Morgen- und Abendroutine, eine Wirkstoffverträglichkeitsprüfung, eine Inhaltsstoffliste – was verwenden und was meiden – sowie konkrete Produktempfehlungen.' },
      { q: 'Wann erhalte ich den Plan?',
        a: 'Der Bericht wird aus deinen Antworten erstellt und ist in der Regel direkt nach dem Ausfüllen des Formulars fertig. Wir senden den fertigen Bericht per E-Mail.' },
      { q: 'Was, wenn ich meinen Hauttyp nicht kenne?',
        a: 'Kein Problem. Das Formular enthält Hinweise und Folgefragen, um den Typ anhand der Symptome zu bestimmen. Wähle „Weiß nicht" und wir finden es heraus.' },
      { q: 'Funktioniert es für sensible Haut?',
        a: 'Besonders gut. Bei sensibler Haut ist es wichtig, welche Wirkstoffe passen und welche reizen können. Die Analyse ist darauf ausgelegt, Letzteres zu vermeiden und sanfte, aber wirksame Optionen auszuwählen.' },
    ],
  },

  finalCta: {
    eyebrow: '5 Minuten – und der Plan gehört dir',
    titleA:  'Zeit für Klarheit',
    titleB:  'über dein Regal',
    body:    '5 Minuten – und ein Plan für deine Haut landet in deinem Postfach.\nKein Internetrat, sondern konkrete Schritte für das, was du bereits besitzt.',
    cta:     'Meinen Plan holen',
    trust:   '12.000+ erstellte Pläne',
  },

  form: {
    pageH1A:    'Lass uns',
    pageH1B:    'deine persönliche Routine erstellen',
    pageSub:    'Erzähl uns ein wenig von dir, und wir beginnen mit der Vorbereitung.',
    progress:   'Fortschritt',
    stepLabel:  'Schritt {n} von {total}',
    back:       'Zurück',
    next:       'Weiter',
    submit:     'Meinen Plan erstellen — {price}',
    fineprint:  'Vertraulich · Plan per E-Mail · 100% Rückerstattung, wenn es nicht passt',

    skinTypes: {
      dry: 'Trocken', oily: 'Fettig', combo: 'Mischhaut',
      sensitive: 'Sensibel', unknown: 'Weiß nicht',
    },

    skin: {
      eyebrowSkinType: 'Hauttyp: {type}',
      title:           'Erzähl uns ein wenig über deine Haut',
      subtitle:        'Typ, Zustand, was du magst und nicht magst – je mehr Details, desto genauer der Plan.',
      placeholder:     'Zum Beispiel: Haut ist trocken, morgens gespannt, manchmal Rötungen auf den Wangen.',
      hint:            'Ein paar Worte reichen. Je mehr Details, desto besser.',
    },

    allergies: {
      title:        'Allergien oder Einschränkungen?',
      subtitle:     'Damit wir sie gezielt aus deinem Plan ausschließen können.',
      yes:          'Ja, habe ich',
      no:           'Nein, alles gut',
      labelDetails: 'Worauf bist du allergisch?',
      placeholder:  'Zum Beispiel: Vitamin C, ätherische Öle, Nüsse...',
      okMessage:    'Super – dieser Schritt wird übersprungen.',
    },

    products: {
      title:           'Erzähl uns von deinen aktuellen Produkten?',
      subtitle:        'Schreib sie auf, lade ein Foto hoch oder lass es leer.',
      labelText:       'Deine Produkte auflisten',
      placeholderText: 'Zum Beispiel: CeraVe Reiniger, La Roche-Posay Creme, Vitamin-C-Serum...',
      orDivider:       'und / oder',
      labelPhoto:      'Foto hochladen',
      dropzonePrompt:  'Foto hier ablegen oder Datei auswählen',
      removeFile:      'Datei entfernen',
      hint:            'Ein einzelnes Foto der Fläschchen reicht – achte darauf, dass die Etiketten lesbar sind.',
    },

    contact: {
      title:       'Wohin sollen wir den Plan schicken?',
      subtitle:    'E-Mail – wir schicken den Bericht in wenigen Minuten.',
      placeholder: 'beispiel@gmail.com',
      hint:        'Wird nur zum Versenden deines Berichts verwendet. Kein Spam.',
    },

    loading: {
      title:    'Dein Hautprofil wird analysiert…',
      subtitle: 'Das dauert nur einen Moment.',
    },

    limit: {
      titleA:   'Wir haben das Limit',
      titleB:   'für neue Anfragen erreicht',
      body:     'Unser Algorithmus nimmt aufgrund hoher Auslastung vorübergehend keine neuen Anfragen an. Wir sind bald zurück – schau später nochmal rein 🧡',
      cta:      'Zurück zur Startseite',
      footer:   'Wir kündigen auf der Website an, sobald wieder ein Platz für neue Pläne frei ist.',
    },
  },

  example: {
    backHome: 'Zurück zur Startseite',
    eyebrow:  'Persönlicher Bericht',
    title:    'SkinBloom Beispielbericht',
    subtitle: 'Erstellt aus der Analyse deiner Haut und deinen Antworten im Formular.',
    meta: {
      date:   'Erstellt am',
      dateValue: '24. Mai 2024',
      id:     'Berichts-ID',
      idValue: 'SB-240524-001',
    },
    score: {
      label:    'Gesamtindex Haut',
      good:     'Ausgezeichnet',
      heading:  'Deine Haut ist in guter Verfassung',
      body:     'Halte deine aktuelle Routine aufrecht und achte auf die Verbesserungsbereiche.',
      scale:    ['Niedrig', 'Mittel', 'Ausgezeichnet'],
    },
    sections: {
      analysis:        'Analyse deiner Haut',
      recommendations: 'Formel- und Produktempfehlungen',
      detailedPlan:    'Detaillierter Pflegeplan',
      stages:          'Produkte nach Phase',
      forecast:        'Erwartete Veränderungen',
    },
    profile: {
      title: 'Hautprofil',
      rows: [
        { label: 'Hauttyp',       value: 'Trocken' },
        { label: 'Sensibilität',  value: 'Hoch' },
        { label: 'Rötungen',      value: 'Auf den Wangen' },
        { label: 'Spannungsgefühl', value: 'Nach der Reinigung' },
        { label: 'Reaktivität',   value: 'Auf Wirkstoffe' },
      ],
    },
    zones: {
      title: 'Verbesserungsbereiche',
      sub:   'Worauf der Plan abzielt',
      items: ['Rötungen', 'Spannungsgefühl', 'Hautbarriere', 'Sensibilität'],
      tipLabel: 'Tipp:',
      tipBody:  'Ein sanfter Reiniger und eine barrierereparierende Creme helfen bei allen Bereichen gleichzeitig.',
    },
    carePlan: {
      title:   'Pflegeplan',
      sub:     'Morgen · Abend',
      steps:   ['Reinigen', 'Niacinamid', 'Feuchtigkeitspflege', 'LSF'],
      cta:     'Vollständigen Plan ansehen',
    },
    ingredients: {
      title: 'Schlüsselinhaltsstoffe',
      items: [
        { name: 'Niacinamid (2–5%)',    note: 'Reduziert Rötungen, stärkt die Barriere' },
        { name: 'Ceramide',              note: 'Bauen die Schutzschicht wieder auf' },
        { name: 'Hyaluronsäure',         note: 'Befeuchtet, reduziert Spannungsgefühl' },
        { name: 'Panthenol (B5)',        note: 'Beruhigt Reizungen' },
      ],
      extra: '+ Zinkgluconat – bei gelegentlichen Unreinheiten',
    },
    productRecs: {
      title: 'Muss ersetzt werden',
      swaps: [
        { from: 'TO Niacinamide 10% + Zinc',   to: 'Geek & Gorgeous Stress Less' },
        { from: 'La Roche-Posay Effaclar H',   to: 'LRP Cicaplast B5 Light' },
        { from: null,                           to: 'Balea Panthenol Serum' },
        { from: null,                           to: 'TO Ascorbyl Glucoside' },
      ],
    },
    tips: {
      title: 'Tipps und Empfehlungen',
      items: [
        'Trage täglich LSF auf, auch im Winter',
        'Überspringe die Feuchtigkeitspflege nicht – die Barriere braucht Pflege',
        'Vermeide Niacinamid 10%+ bei sensibler Haut',
        'Führe neue Produkte einzeln ein, alle zwei Wochen',
        'Sanftes Vitamin C – morgens, dreimal pro Woche',
      ],
    },
    detailed: {
      eyebrow: 'Schritt für Schritt',
      heading: 'Dein endgültiger Pflegeplan',
      tabs: { morning: 'Morgen', evening: 'Abend', weekly: 'Nach Woche' },
      stepTitles: {
        cleanse:     'Reinigung',
        niacin:      'Niacinamid 2–5%',
        moisturise:  'Feuchtigkeitspflege',
        spf:         'LSF',
        panthenol:   'Panthenol (2–5%)',
        creamRepair: 'Regenerierende Creme',
        vitaminC:    'Mo/Mi/Fr — Vitamin C',
      },
      productNotes: {
        niacin10Strong:  'TO Niacinamide 10% — zu stark',
        effaclarMorning: 'Effaclar H — nicht geeignet',
        effaclarEvening: 'Effaclar H — unzureichend',
      },
    },
    stage: {
      shouldHave: 'Sollte enthalten',
      avoid:      'Vermeiden',
      yourProducts: 'Deine Produkte',
      examples:     'Beispielprodukte',
      noProduct:    'Kein geeignetes Produkt',
      stages: [
        {
          title: 'Reinigung (morgens und abends)',
          shouldHave: ['Milde Tenside', 'Keine Alkohole oder Duftstoffe', 'Ceramide / Glycerin / Aminosäuren'],
          avoid: ['Saure Toner', 'Starke Gele für Problemhaut', 'Stark schäumende Formeln'],
          yourProducts: [{ name: 'CeraVe Hydrating Cleanser', ok: true }, { name: 'Pixi Glow Tonic', ok: false }],
          examples: [
            { tier: 'Budget',      items: ['CeraVe Hydrating Cleanser', 'Simple Refreshing Facial Wash'] },
            { tier: 'Mittelklasse', items: ['LRP Toleriane Dermo Cleanser', 'Bioderma Sensibio Foaming Gel'] },
          ],
        },
        {
          title: 'Niacinamid (morgens)',
          shouldHave: ['2–5% Konzentration', 'Wenig oder kein Zink', 'Beruhigende Zusätze'],
          avoid: ['10%+ Konzentrationen', 'Kombos mit Säuren', 'Stechende Formeln'],
          yourProducts: [{ name: 'TO Niacinamide 10% + Zinc', ok: false }],
          examples: [
            { tier: 'Budget',      items: ['TO Niacinamide 5%', 'Revolution 2% Niacinamide'] },
            { tier: 'Mittelklasse', items: ['G&G Stress Less', 'LRP Mela B3'] },
          ],
        },
        {
          title: 'Feuchtigkeitspflege (morgens und abends)',
          shouldHave: ['Ceramide', 'Cholesterin', 'Fettsäuren', 'Leichte Textur'],
          avoid: ['Formeln für fettige Haut', 'Alkohole', 'Saure Inhaltsstoffe'],
          yourProducts: [{ name: 'La Roche-Posay Effaclar H', ok: false }],
          examples: [
            { tier: 'Budget',      items: ['CeraVe Moisturising Cream', 'Balea Cica Cream'] },
            { tier: 'Mittelklasse', items: ['LRP Cicaplast B5 Light', 'Bioderma Atoderm Gel-Crème'] },
          ],
        },
        {
          title: 'LSF (morgens)',
          shouldHave: ['LSF 50', 'Leichte Textur', 'Ohne Duftstoffe', 'Für sensible Haut geeignet'],
          avoid: [],
          yourProducts: [{ name: 'Garnier Ambre Solaire Fluid SPF50', ok: true }],
          examples: [
            { tier: 'Mittelklasse', items: ['LRP UVMune 400'] },
            { tier: 'Premium',      items: ['SkinCeuticals Ultra Facial Defense'] },
          ],
        },
        {
          title: 'Panthenol (abends)',
          shouldHave: ['2–5% Panthenol', 'Keine Alkohole', 'Keine Säuren'],
          avoid: [],
          yourProducts: [],
          examples: [
            { tier: 'Budget',      items: ['Balea Panthenol Serum'] },
            { tier: 'Mittelklasse', items: ['LRP Cicaplast B5 Serum'] },
          ],
        },
        {
          title: 'Vitamin C (an einigen Morgen)',
          shouldHave: ['Stabile Formen (SAP/MAP/AG)', '5–10% Konzentration', 'Keine reizenden Zusätze'],
          avoid: [],
          yourProducts: [],
          examples: [
            { tier: 'Budget',      items: ['TO Ascorbyl Glucoside 12%'] },
            { tier: 'Mittelklasse', items: ['G&G C-Glow'] },
          ],
        },
      ],
    },
    timeline: {
      eyebrow: 'Wenn du beim Plan bleibst',
      heading: 'Erwartete Veränderungen',
      steps: [
        { period: '1–2 Wochen', num: '01', items: ['Weniger Spannungsgefühl', 'Haut wirkt ruhiger'] },
        { period: '4 Wochen',   num: '02', items: ['Rötungen weniger sichtbar', 'Haut weicher und gleichmäßiger'] },
        { period: '6–8 Wochen', num: '03', items: ['Barriere wiederhergestellt', 'Sensibilität reduziert', 'Stabiler Hautzustand'] },
      ],
    },
    finalCta: {
      eyebrow: 'Persönlicher Plan',
      heading: 'Möchtest du deinen eigenen persönlichen Plan?',
      body:    'Oben siehst du einen Beispielbericht. Deiner wird auf deine Haut und deine Produkte zugeschnitten.',
      cta:     'Meinen Pflegeplan holen',
    },
    confidential: 'Der Bericht ist privat – nur du kannst ihn sehen',
  },
};
